import React from 'react';
import styled from 'styled-components';
import { sectionShell, sectionHeader, sectionTitle, sectionDescription } from '../../styles/sectionStyles';
import { motion } from 'framer-motion';
import {
  FaBriefcase,
  FaCalendarAlt,
  FaCheckCircle,
  FaCode,
  FaDownload,
  FaGraduationCap,
} from 'react-icons/fa';
import { Bio, education, experiences } from '../../data/constants';

const Container = styled.section`
  ${sectionShell}
`;

const Wrapper = styled.div`
  width: min(1180px, 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 34px;
`;

const Header = styled.div`
  ${sectionHeader}
`;

const Eyebrow = styled.div`
  color: var(--accent);
  font-size: 12px;
  font-weight: 650;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  margin-bottom: 12px;
`;

const Title = styled.h2`
  ${sectionTitle}
`;

const Accent = styled.span`
  color: var(--accent);
`;

const Desc = styled.p`
  ${sectionDescription}
`;

const ContentGrid = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1.12fr) minmax(320px, 0.56fr);
  gap: 26px;
  align-items: start;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const TimelineCard = styled.article`
  position: relative;
  padding: 26px 32px 26px 76px;
  border-radius: 18px;
  background: var(--surface);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-card);

  &::before {
    content: '';
    position: absolute;
    left: 37px;
    top: 50px;
    bottom: 56px;
    width: 2px;
    background: var(--surface);
    opacity: 0.55;
  }

  @media (max-width: 620px) {
    padding: 22px 18px 22px 48px;

    &::before {
      left: 25px;
    }
  }
`;

const TimelineItem = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr);
  gap: 24px;
  padding: ${({ $last }) => $last ? '0' : '0 0 25px'};
  margin-bottom: ${({ $last }) => $last ? '0' : '25px'};
  border-bottom: ${({ $last }) => $last ? 'none' : '1px solid var(--border)'};

  &::before {
    content: '';
    position: absolute;
    left: -43px;
    top: 20px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: ${({ $muted }) => $muted ? 'var(--muted)' : 'var(--accent)'};
    box-shadow: 0 0 0 4px rgba(249, 115, 22, 0.12);
  }

  @media (max-width: 760px) {
    grid-template-columns: 58px 1fr;
    gap: 16px;
  }
`;

const LogoBox = styled.div`
  width: 72px;
  height: 72px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: var(--surface);
  border: 1px solid var(--border);
  overflow: hidden;

  img {
    width: 52px;
    height: 52px;
    object-fit: cover;
    border-radius: 10px;
  }

  svg {
    color: var(--muted);
    font-size: 36px;
  }

  @media (max-width: 760px) {
    width: 56px;
    height: 56px;

    img {
      width: 42px;
      height: 42px;
    }
  }
`;

const ItemMain = styled.div`
  min-width: 0;
`;

const Role = styled.h3`
  margin: 0;
  color: var(--text);
  font-size: 18px;
  line-height: 1.25;
  font-weight: 650;
`;

const Company = styled.div`
  margin-top: 6px;
  color: var(--accent);
  font-size: 14px;
  font-weight: 600;
`;

const Detail = styled.p`
  max-width: 460px;
  margin: 12px 0 0;
  color: var(--muted);
  font-size: 14px;
  line-height: 1.48;
  font-weight: 500;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const SkillList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
  margin-top: 13px;
`;

const Skill = styled.span`
  padding: 5px 11px;
  border-radius: 8px;
  color: ${({ $index }) => ['var(--blue)', 'var(--green)', 'var(--accent)', 'var(--teal)'][$index % 4]};
  background: ${({ $index }) => ['var(--blue-bg)', 'var(--green-bg)', 'var(--orange-bg)', 'var(--teal-bg)'][$index % 4]};
  font-size: 12px;
  font-weight: 600;
`;

const MetaCol = styled.div`
  grid-column: 2;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px;
  color: var(--muted);
  font-size: 13px;
  font-weight: 700;

  @media (max-width: 760px) {
    grid-column: 2;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 12px;
  }
`;

const MetaItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;

  svg {
    color: var(--muted);
  }
`;

const Status = styled.div`
  width: fit-content;
  padding: 6px 12px;
  border-radius: 999px;
  color: var(--green);
  background: var(--green-bg);
  font-size: 12px;
  font-weight: 650;
`;

const SideColumn = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SideCard = styled.article`
  padding: 26px;
  border-radius: 18px;
  background: var(--surface);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-card);
`;

const SideHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
`;

const SideIcon = styled.div`
  width: 42px;
  height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  color: ${({ $tone }) => $tone === 'purple' ? 'var(--purple)' : $tone === 'green' ? 'var(--green)' : 'var(--accent)'};
  background: ${({ $tone }) => $tone === 'purple' ? 'var(--purple-bg)' : $tone === 'green' ? 'var(--green-bg)' : 'var(--orange-bg)'};
  font-size: 18px;
`;

const SideTitle = styled.h3`
  margin: 0;
  color: var(--text);
  font-size: 17px;
  font-weight: 650;
`;

const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
`;

const OverviewStat = styled.div`
  text-align: center;
  padding: 8px 0;

  & + & {
    border-left: 1px solid var(--border);
  }
`;

const StatValue = styled.div`
  color: var(--accent);
  font-size: 32px;
  line-height: 1;
  font-weight: 650;
`;

const StatLabel = styled.div`
  margin-top: 10px;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.45;
`;

const DoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  color: var(--muted);
  font-size: 14px;
  font-weight: 700;
`;

const DoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    color: var(--green);
  }
`;

const EducationTitle = styled.h4`
  margin: 0;
  color: var(--text);
  font-size: 15px;
  line-height: 1.35;
  font-weight: 650;
`;

const EducationText = styled.div`
  margin-top: 12px;
  color: var(--muted);
  font-size: 14px;
  font-weight: 600;
`;

const ResumeButton = styled.a`
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 0 34px;
  border-radius: 999px;
  color: var(--text);
  background: var(--surface);
  border: 1px solid var(--border);
  box-shadow: 0 16px 34px rgba(72, 50, 28, 0.08);
  text-decoration: none;
  font-size: 15px;
  font-weight: 650;
  transition: transform 160ms ease, border-color 160ms ease;

  &:hover {
    transform: translateY(-2px);
    border-color: var(--accent);
  }
`;

const cleanDate = (date = '') => date.replace(/-/g, '-');

const Experience = () => {
  const mainEducation = education[0];
  const timelineItems = experiences;

  return (
    <Container id="experience">
      <Wrapper>
        <Header as={motion.div} initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.5, ease: 'easeOut' }}>
          <Eyebrow>My Experience</Eyebrow>
          <Title>Professional <Accent>Experience</Accent></Title>
          <Desc>Through two software engineering internships, I worked on client applications, backend APIs, database workflows, and AI integrations.</Desc>
        </Header>

        <ContentGrid>
          <TimelineCard as={motion.article} initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }} transition={{ duration: 0.5, ease: 'easeOut' }}>
            {timelineItems.map((item, index) => {
              const isLast = index === timelineItems.length - 1;
              return (
                <TimelineItem key={item.id} $last={isLast} $muted={item.education}>
                  <LogoBox>
                    {item.education ? <FaGraduationCap /> : item.img && <img src={item.img} alt={`${item.company} logo`} loading="lazy" />}
                  </LogoBox>

                  <ItemMain>
                    <Role>{item.role}</Role>
                    <Company>{item.company}</Company>
                    <Detail as="ul" style={{ display: 'block', paddingLeft: 18 }}>{(item.responsibilities || [item.desc]).map(text => <li key={text} style={{ marginBottom: 8 }}>{text}</li>)}</Detail>
                    <SkillList>
                      {(item.skills || []).map((skill, skillIndex) => (
                        <Skill key={`${item.id}-${skill}`} $index={skillIndex}>{skill}</Skill>
                      ))}
                    </SkillList>
                  </ItemMain>

                  <MetaCol>
                    {/present/i.test(item.date || '') && <Status>Current</Status>}
                    <MetaItem><FaCalendarAlt /> {cleanDate(item.date)}</MetaItem>
                  </MetaCol>
                </TimelineItem>
              );
            })}
          </TimelineCard>

          <SideColumn>
            <SideCard as={motion.article} initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.45, ease: 'easeOut' }}>
              <SideHeader>
                <SideIcon><FaBriefcase /></SideIcon>
                <SideTitle>Experience Overview</SideTitle>
              </SideHeader>
              <OverviewGrid>
                <OverviewStat>
                  <StatValue>{experiences.length}</StatValue>
                  <StatLabel>Engineering<br />Internships</StatLabel>
                </OverviewStat>
                <OverviewStat>
                  <StatValue>{experiences.length}</StatValue>
                  <StatLabel>Companies<br />Contributed To</StatLabel>
                </OverviewStat>
              </OverviewGrid>
            </SideCard>

            <SideCard as={motion.article} initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.45, delay: 0.06, ease: 'easeOut' }}>
              <SideHeader>
                <SideIcon $tone="green"><FaCode /></SideIcon>
                <SideTitle>What I Do</SideTitle>
              </SideHeader>
              <DoList>
                {['Full-Stack Web Development', 'API Development & Integration', 'Database Design & Optimization', 'AI & Automation Solutions', 'Problem Solving & Debugging'].map((item) => (
                  <DoItem key={item}><FaCheckCircle /> {item}</DoItem>
                ))}
              </DoList>
            </SideCard>

            <SideCard as={motion.article} initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.45, delay: 0.12, ease: 'easeOut' }}>
              <SideHeader>
                <SideIcon $tone="purple"><FaGraduationCap /></SideIcon>
                <SideTitle>Education</SideTitle>
              </SideHeader>
              <EducationTitle>{mainEducation.degree}</EducationTitle>
              <EducationText>{mainEducation.school}</EducationText>
              <EducationText><FaCalendarAlt /> {mainEducation.date}</EducationText>
            </SideCard>
          </SideColumn>
        </ContentGrid>

        <ResumeButton href={Bio.resume} target="_blank" rel="noopener noreferrer">
          <FaDownload /> Download Resume
        </ResumeButton>
      </Wrapper>
    </Container>
  );
};

export default Experience;
